import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Attempts after the first one. Two is enough to ride out an instance handover; more only delays
 *  telling the user that something is genuinely wrong. */
const MAX_RETRIES = 2;
/** First backoff; the second wait is 3× this. Short on purpose — the user is staring at a spinner. */
const BASE_DELAY_MS = 600;

/**
 * Gateway/infrastructure conditions where the request provably did not reach our handler, so
 * repeating it cannot double-apply anything: 503/504 come from the Functions host before an
 * instance picked the request up (Flex Consumption scale-out), 502 from the front end, 429 is an
 * explicit "not now", 408 a request timeout.
 */
const TRANSIENT_STATUSES = new Set([408, 429, 502, 503, 504]);

/** Methods that are safe to repeat even when the outcome of the first attempt is unknown. */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

function isApiRequest(url: string): boolean {
  const apiUrl = environment.apiurl;
  if (!apiUrl) {
    return false;
  }

  try {
    const origin = window.location.origin;
    const request = new URL(url, origin);
    const api = new URL(apiUrl, origin);
    return request.origin === api.origin && request.pathname.startsWith(api.pathname);
  } catch {
    return false;
  }
}

function isRetryable(req: HttpRequest<unknown>, error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  // No response reached us at all — safe to repeat only when repeating is harmless.
  if (error.status === 0) {
    return IDEMPOTENT_METHODS.has(req.method.toUpperCase());
  }

  return TRANSIENT_STATUSES.has(error.status);
}

/**
 * Retries API calls that failed for reasons that have nothing to do with the request itself.
 *
 * Without this a single cold-start 503 or a dropped connection surfaces straight in the UI as
 * "konnte nicht gespeichert werden — bitte erneut versuchen", and the user's second click is what
 * actually saves. The command endpoints only enqueue onto the command queue, so the work the user
 * waits for happens well after this response; a lost or refused HTTP attempt is by far the most
 * likely way a save appears to fail.
 *
 * Deliberately conservative about what it repeats:
 *  - status 0 (no response at all — connection dropped, DNS/TLS failure) is retried only for
 *    idempotent methods. For a POST it is genuinely ambiguous whether the server processed the
 *    request before the connection died, and repeating one would create a second entity.
 *  - a status the server produced itself (400/401/403/404/409/500 …) is never retried: it reached
 *    our code and repeating it just reproduces the same answer — or, for a 500 out of the command
 *    API, risks enqueueing the same command twice.
 */
export const transientRetryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: unknown, retryCount: number) => {
        if (!isRetryable(req, error)) {
          return throwError(() => error);
        }
        return timer(BASE_DELAY_MS * Math.pow(3, retryCount - 1));
      },
    }),
  );
};
